import { createFeature, createReducer, on } from "@ngrx/store";
import { BankAccountErrorData, BankAccountOptions } from "../../interfaces/bank-account-form";
import * as BankAccountActions from './bank-account-form.actions';
import { BankAccount } from "../../interfaces";

/**
 * Initial state of the Bank Account Form feature.
 *
 * @const initialState
 * @type {BankAccountOptions}
 */
const initialState: BankAccountOptions = {
    bankAccountData: {
        bankName: '',
        bankAccountName: '',
        routingNumber: '',
        bankAccountNumber: '',
    },
    bankAccountErrorData: {
        serviceCode: '',
        errorCode: '',
        requestId: '',
        message: ''
    },
    loading: false,
    routingBankNameError: false,
    isSubmittingSuccess: false,
    isSubmittingError: false
};

/**
 * Handles the `fetchBankInfoData` action.
 * Resets the loading and error states.
 */
const fetchBankInfoFunction: any =
    (state: BankAccountOptions) => ({ ...state, loading: false, isSubmittingError: false });

/**
 * Handles the `fetchBankInfoDataSuccess` action.
 * Updates the state with the fetched bank name and routing number.
 */
const fetchBankInfoDataSuccessFunction: any =
    (state: BankAccountOptions, action: { routingNumber: string, bankName: string }) => (
        {
            ...state,
            bankAccountData: {
                ...state.bankAccountData, bankName: action.bankName, routingNumber: action.routingNumber
            }, loading: false, isSubmittingError: false, routingBankNameError: false, bankAccountErrorData: { ...initialState.bankAccountErrorData }
        });

/**
 * Handles the `fetchBankInfoDataError` action.
 * Updates the state with error details and resets the bank name and routing number.
 */
const fetchBankInfoDataErrorFunction: any =
    (state: BankAccountOptions, action: { bankAccountErrorData: BankAccountErrorData }) => {
        console.log(action.bankAccountErrorData);
        return (
            {
                ...state,
                bankAccountData: {
                    ...state.bankAccountData, bankName: '', routingNumber: ''
                }, loading: false, isSubmittingError: false, routingBankNameError: true,
                bankAccountErrorData: { ...action.bankAccountErrorData }
            }
        )
    };

/**
 * Handles the `resetFetchBankInfoDataError` action.
 * 
 * Resets the error state related to fetching bank information. This is typically used
 * when the user modifies the routing number or resets the form to clear any existing errors.
 * 
 * @param state - The current state of the `BankAccountOptions`.
 * @returns A new state with the error-related fields reset to their initial values.
 */
const resetFetchBankInfoDataErrorFunction: any =
    (state: BankAccountOptions) => ({ 
        ...state, 
        loading: false, 
        isSubmittingError: false, 
        routingBankNameError: false, 
        bankAccountErrorData: { ...initialState.bankAccountErrorData } 
    });

/**
 * Handles the `updateBankInformationData` action.
 * Updates the state with the provided bank account data and sets the loading state.
 */
const updateBankInformationDataFunction: any =
    (state: BankAccountOptions, action: { bankData: BankAccount }) => ({ ...state, bankAccountData: { ...action.bankData }, loading: true, isSubmittingError: false, routingBankNameError: false });

/**
 * Handles the `updateBankInformationDataSuccess` action.
 * Resets the loading and error states and sets the submission success state.
 */
const updateBankInformationDataSuccessFunction: any =
    (state: BankAccountOptions) => ({ ...state, loading: false, isSubmittingError: false, routingBankNameError: false, isSubmittingSuccess: true });

/**
 * Handles the `updateBankInformationDataError` action.
 * Updates the state to indicate a submission error.
 */
const updateBankInformationDataErrorFunction: any =
    (state: BankAccountOptions) => ({ ...state, loading: false, isSubmittingError: true, routingBankNameError: false });

/**
 * Handles the `resetSubmittingBankInformationForm` action.
 * Resets the state to its initial values.
 */
const resetSubmittingBankInformationFormFunction: any =
    (state: BankAccountOptions) => ({ ...initialState });

/**
 * Reducer function to handle state transitions for the Bank Account Form feature.
 *
 * @const bankAccountFormReducer
 */
const bankAccountFormReducer = createReducer(
    initialState,
    on(BankAccountActions.fetchBankInfoData, fetchBankInfoFunction),
    on(BankAccountActions.fetchBankInfoDataSuccess, fetchBankInfoDataSuccessFunction),
    on(BankAccountActions.fetchBankInfoDataError, fetchBankInfoDataErrorFunction),
    on(BankAccountActions.resetFetchBankInfoDataError, resetFetchBankInfoDataErrorFunction),
    on(BankAccountActions.updateBankInformationData, updateBankInformationDataFunction),
    on(BankAccountActions.updateBankInformationDataSuccess, updateBankInformationDataSuccessFunction),
    on(BankAccountActions.updateBankInformationDataError, updateBankInformationDataErrorFunction),
    on(BankAccountActions.resetSubmittingBankInformationForm, resetSubmittingBankInformationFormFunction)
);

/**
 * Feature configuration for the Bank Account Form.
 * Defines the feature name and its associated reducer.
 *
 * @const BankAccountFormFeature
 */
export const BankAccountFormFeature = createFeature({
    name: 'bankAccountForm',
    reducer: bankAccountFormReducer
});
